import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { WorkflowPage } from './components/WorkflowPage';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchAllDatabaseData } from '../../store/slices/databaseSlice';
import { resetCreateCR, hydrateFromSavedCR } from '../../store/slices/createCRSlice';
import { constructionReviewsService, ConstructionReview } from '../../services/construction-reviews.service';
import { ROUTES, SYSTEM_KEYS } from '../../constants';

export const CreateCRPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: urlCRId } = useParams<{ id: string }>();
  const { is_initialized } = useAppSelector((state) => state.database);
  const formData = useAppSelector((state) => state.createCR.form_data);
  const hydratedRef = useRef(false);

  // Support both URL param (/create/:id) and legacy navigation state
  const resumeCRId = urlCRId || (location.state as { resumeCRId?: string })?.resumeCRId;

  useEffect(() => {
    // Load database data if not already loaded
    if (!is_initialized) {
      dispatch(fetchAllDatabaseData());
    }
  }, [dispatch, is_initialized]);

  // Always reset when entering Create CR — then hydrate if resuming a saved CR
  useEffect(() => {
    // Reset previous state unconditionally to avoid stale data
    dispatch(resetCreateCR());
    localStorage.removeItem('cr_form_state');
    hydratedRef.current = false;

    if (resumeCRId) {
      hydratedRef.current = true;
      constructionReviewsService.getById(resumeCRId).then((cr: ConstructionReview) => {
        dispatch(hydrateFromSavedCR({
          task_id: cr.task_id,
          task_name: cr.task_name,
          structured_data: cr.structured_data || {},
          aurora_full_data: cr.aurora_full_data || null,
          cr_output: {
            generated_cr: cr.generated_cr || {},
            updated_cr: cr.updated_cr || {},
            changes: cr.changes || {},
          },
        }));
      });
    }
  }, [dispatch, resumeCRId]);

  useEffect(() => {
    // Check if there are any changes in the form data
    const hasFormChanges = () => {
      // Check if user has selected a task
      if (formData.task.selected_task) return true;
      
      // Check if user has made any equipment selections across all systems
      const hasEquipmentChanges = SYSTEM_KEYS.some(key => {
        const sys = formData.equipment[key];
        return !!(
          sys?.module?.selected_brand ||
          sys?.module?.selected_module ||
          (sys?.module?.quantity ?? 0) > 0 ||
          sys?.inverters?.selected_brand ||
          (sys?.inverters?.selected_inverters?.length ?? 0) > 0 ||
          sys?.batteries?.selected_brand ||
          sys?.batteries?.selected_battery
        );
      });
      if (hasEquipmentChanges) return true;
      
      if (formData.structural.structures?.structure_1?.framing?.size ||
          formData.structural.structures?.structure_1?.framing?.type ||
          formData.structural.structures?.structure_1?.framing?.rafter_spacing) return true;
      
      // Check documentation data
      const docData = formData.documentation;
      if (docData.ahj_value ||
          docData.um_number_value || docData.install_notes_value) return true;
      
      return false;
    };

    // Add beforeunload event listener for page refresh warning
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasFormChanges()) {
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    // Add popstate event listener for browser back/forward buttons
    const handlePopState = () => {
      if (hasFormChanges()) {
        const shouldLeave = window.confirm(
          'You have unsaved changes that will be lost. Do you want to reset the form and return to the home page?'
        );
        
        if (shouldLeave) {
          // Reset the form to initial state
          dispatch(resetCreateCR());
          // Clear localStorage
          localStorage.removeItem('cr_form_state');
          // Navigate to home page
          navigate(ROUTES.HOME, { replace: true });
        } else {
          // Push current state back to prevent navigation
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Push initial state to handle back button
    window.history.pushState(null, '', window.location.pathname);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch, navigate]);
  
  return <WorkflowPage />;
};